import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

import styles from '@vp-ui/components/VpVideoBrandingPreview/VpVideoBrandingPreview.module.less';

import {
  TITLE_TOP_MARGIN_PCT,
  TITLE_FONT_SIZE_PCT,
  TITLE_BOX_PADDING_PCT,
  TITLE_FONT_FAMILY,
  TITLE_SIDE_MARGIN_PCT,
  TITLE_POSITIONS,
  TITLE_POSITION_VALUES,
  WATERMARK_MARGIN_PCT,
  WATERMARK_POSITIONS,
  WATERMARK_POSITION_VALUES
} from '@src/constants/branding-layout';

const ASPECT_RATIOS = {
  landscape: '16 / 9',
  portrait: '9 / 16',
  square: '1 / 1'
};

const pct = (fraction) => `${fraction * 100}%`;
/**
 * Container-query *width* units. Needed wherever the burn derives a distance from the frame
 * width but applies it vertically: a plain percentage would resolve against the height and
 * drift on every non-square frame.
 */
const cqw = (fraction) => `${fraction * 100}cqw`;
/** Container-query *height* units, for values the burn derives from the frame height. */
const cqh = (fraction) => `${fraction * 100}cqh`;

export const watermarkAnchor = (position) => {
  const inset = cqw(WATERMARK_MARGIN_PCT);

  switch (position) {
    case WATERMARK_POSITIONS.TOP_LEFT:
      return { top: inset, left: inset };
    case WATERMARK_POSITIONS.TOP_RIGHT:
      return { top: inset, right: inset };
    case WATERMARK_POSITIONS.BOTTOM_LEFT:
      return { bottom: inset, left: inset };
    case WATERMARK_POSITIONS.BOTTOM_RIGHT:
    default:
      return { bottom: inset, right: inset };
  }
};

/**
 * Geometry of the title banner, as a style object.
 *
 * Exported so it can be asserted directly: `toHaveStyle` cannot resolve emotion's injected
 * rules (and jsdom does not understand container-query units at all), so testing the numbers
 * through the DOM would prove nothing. These are the values that must match the burn.
 */
/**
 * Where the title banner sits, mirroring the ASS alignment the burn uses.
 *
 * The vertical inset is the same number top or bottom, exactly as libass measures MarginV from
 * whichever edge the alignment anchors to. Centred positions keep the translateX trick; corners
 * anchor to the side inset with no transform, which is how libass places a corner-aligned line.
 */
export const titleAnchor = (position) => {
  const vertical = String(position).startsWith('bottom')
    ? { bottom: pct(TITLE_TOP_MARGIN_PCT) }
    : { top: pct(TITLE_TOP_MARGIN_PCT) };

  if (String(position).endsWith('_left')) {
    return { ...vertical, left: cqw(TITLE_SIDE_MARGIN_PCT) };
  }

  if (String(position).endsWith('_right')) {
    return { ...vertical, right: cqw(TITLE_SIDE_MARGIN_PCT) };
  }

  return { ...vertical, left: '50%', transform: 'translateX(-50%)' };
};

export const titleStyle = (title) => ({
  position: 'absolute',
  // The *banner* starts at the shared margin. The burn reaches the same place from the other
  // side: libass's MarginV positions the top of the text and BorderStyle=3 paints the box above
  // it, so the burn adds the padding to MarginV. Here the box and its padding are positioned
  // together — applying the burn's formula would push the banner down by one padding and make
  // the preview lie.
  ...titleAnchor(title.position || TITLE_POSITIONS.TOP_CENTER),
  maxWidth: '90%',
  fontFamily: TITLE_FONT_FAMILY,
  fontSize: cqh(TITLE_FONT_SIZE_PCT),
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Padding is a fraction of the font size, which is itself a fraction of the height —
  // expressing it in `em` keeps that relationship without restating it.
  padding: `${TITLE_BOX_PADDING_PCT}em`,
  color: title.fontColor,
  backgroundColor: title.background
});

/** Geometry of the watermark logo, as a style object. See titleStyle for why this is exported. */
export const watermarkStyle = (watermark) => ({
  position: 'absolute',
  // `scale` is a fraction of the video width in the burn, so a plain width percentage
  // matches it exactly; height stays auto to preserve the logo's aspect ratio, mirroring
  // the burn's `scale=W:-1`.
  width: pct(watermark.scale),
  height: 'auto',
  opacity: watermark.opacity,
  ...watermarkAnchor(watermark.position)
});

/** Aspect ratio of the preview frame, from an explicit orientation. */
export const frameAspectRatio = (orientation) => ASPECT_RATIOS[orientation] || ASPECT_RATIOS.landscape;

/**
 * Aspect ratio the burn will actually produce, for a given crop and source video.
 *
 * `16:9` is the burner's *baseline*, not a crop: CROP_FILTERS maps it to null, no filter runs
 * and the output keeps the source's own dimensions. Drawing a fixed landscape frame for it
 * would show a portrait phone video as widescreen while the burn produced portrait — the
 * exact case the staging checklist calls out. The other two ratios do crop, so they define
 * the frame regardless of the source.
 */
export const previewAspectRatio = (crop, sourceDimensions) => {
  if (crop === '9:16') return ASPECT_RATIOS.portrait;
  if (crop === '1:1') return ASPECT_RATIOS.square;

  if (sourceDimensions?.width && sourceDimensions?.height) {
    return `${sourceDimensions.width} / ${sourceDimensions.height}`;
  }

  return ASPECT_RATIOS.landscape;
};

/**
 * Live CSS preview of the title banner and watermark that the burner will render into the
 * video.
 *
 * The video deliberately has no native `controls`. They would cover the bottom watermark with
 * the control bar — precisely where a bottom-anchored logo sits — and their fullscreen button
 * promotes only the `<video>` element, leaving the overlays behind as siblings in the DOM: the
 * user would see a stretched video with no branding at all. This is a geometry preview, not a
 * player; the Preview tab is where the video is watched. Every measurement comes from `constants/branding-layout`, which mirrors the burner's
 * `layout.ts` — that shared source is the only thing keeping the preview honest.
 *
 * The frame declares `containerType: 'size'` so the overlays can be expressed in cqw/cqh,
 * i.e. as fractions of the frame itself, exactly as the burn expresses them as fractions of
 * the video. No measuring, no resize listeners: the browser resolves them at layout time, so
 * the preview stays faithful at any size the dialog gives it.
 */
// Mirrors the caption burn's own geometry: assGenerator uses marginV = resY * 0.14 and
// marginH = resX * 0.10. Kept in step by hand, like the title constants — the generator lives in
// the lambda and cannot be imported here. A band drawn anywhere else would lie about the
// collision it exists to reveal.
export const CAPTION_BAND_BOTTOM_PCT = 0.14;
export const CAPTION_BAND_SIDE_PCT = 0.10;
// Roughly one line of the largest preset at typical scale; the exact height varies with the
// frame, and what matters is the band's lower edge.
export const CAPTION_BAND_HEIGHT_PCT = 0.06;

export const captionBandStyle = {
  position: 'absolute',
  left: pct(CAPTION_BAND_SIDE_PCT),
  right: pct(CAPTION_BAND_SIDE_PCT),
  bottom: cqh(CAPTION_BAND_BOTTOM_PCT),
  height: cqh(CAPTION_BAND_HEIGHT_PCT),
  borderRadius: '4px',
  border: '1px dashed rgba(255,255,255,0.7)',
  backgroundColor: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255,255,255,0.9)',
  fontSize: cqh(0.035),
  pointerEvents: 'none'
};

const VpVideoBrandingPreview = ({
  mediaUrl = null,
  orientation = null,
  crop = null,
  title = null,
  watermark = null,
  showCaptionBand = false
}) => {
  // Measured from the loaded video rather than requested from the API: the element already
  // knows its own dimensions, and this keeps the preview correct for any source without a
  // round trip. Until metadata arrives the frame falls back to landscape.
  const [sourceDimensions, setSourceDimensions] = useState(null);

  const showTitle = !!(title && title.enabled && title.text && title.text.trim());
  const showWatermark = !!(watermark && watermark.enabled && watermark.url);

  // `crop` is the faithful input (it knows 16:9 means "keep the source"); `orientation` stays
  // supported for callers that genuinely want to force a frame.
  const aspectRatio = crop
    ? previewAspectRatio(crop, sourceDimensions)
    : frameAspectRatio(orientation || 'landscape');

  return (
    <div className={styles.vpBrandingPreviewContainer}>
      <div
        className={styles.vpBrandingFrame}
        data-testid="branding-frame"
        style={{ aspectRatio }}
      >
        {mediaUrl && (
          <video
            data-testid="branding-video"
            src={mediaUrl}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) =>
              setSourceDimensions({ width: e.target.videoWidth, height: e.target.videoHeight })
            }
          />
        )}

        {showCaptionBand && (
          <Box data-testid="branding-caption-band" sx={captionBandStyle}>
            caption
          </Box>
        )}

        {showTitle && (
          <Box data-testid="branding-title" sx={titleStyle(title)}>
            {title.text}
          </Box>
        )}

        {showWatermark && (
          <Box
            component="img"
            data-testid="branding-watermark"
            src={watermark.url}
            alt=""
            sx={watermarkStyle(watermark)}
          />
        )}
      </div>
    </div>
  );
};

VpVideoBrandingPreview.propTypes = {
  showCaptionBand: PropTypes.bool,
  mediaUrl: PropTypes.string,
  orientation: PropTypes.oneOf(['landscape', 'portrait', 'square']),
  crop: PropTypes.oneOf(['16:9', '9:16', '1:1']),
  title: PropTypes.shape({
    enabled: PropTypes.bool,
    text: PropTypes.string,
    fontColor: PropTypes.string,
    background: PropTypes.string,
    position: PropTypes.oneOf(TITLE_POSITION_VALUES)
  }),
  watermark: PropTypes.shape({
    enabled: PropTypes.bool,
    url: PropTypes.string,
    position: PropTypes.oneOf(WATERMARK_POSITION_VALUES),
    opacity: PropTypes.number,
    scale: PropTypes.number
  })
};

export default VpVideoBrandingPreview;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpVideoBrandingPreview/VpVideoBrandingPreview.jsx